import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getOrder, type OrderSummary } from '@/lib/checkoutClient';
import { isMockMode, getMockOrder } from '@/lib/mock-data';

/**
 * useOrderPolling — polls GET /taquilla/order/:id every 5 seconds while
 * the order is in a non-terminal state. Stops polling once the order
 * reaches `confirmed | qr_issued | used | failed | cancelled | expired |
 * refunded | partially_refunded | disputed`.
 *
 * Used by /checkout/success (TASK-C08) and indirectly by Checkout.tsx
 * post-payment (TASK-C07) while waiting for the webhook to confirm.
 */
const TERMINAL_STATUSES = new Set([
  'confirmed',
  'qr_issued',
  'used',
  'failed',
  'cancelled',
  'expired',
  'refunded',
  'partially_refunded',
  'disputed',
]);

export function isTerminalOrderStatus(status: string | undefined): boolean {
  return !!status && TERMINAL_STATUSES.has(status);
}

export function useOrderPolling(
  orderId: string | undefined,
  opts: { intervalMs?: number; enabled?: boolean } = {},
): UseQueryResult<OrderSummary> {
  const { intervalMs = 5_000, enabled = true } = opts;
  return useQuery({
    queryKey: ['order', orderId, isMockMode() ? 'mock' : 'live'],
    queryFn: async () => {
      if (!orderId) throw new Error('useOrderPolling: orderId required');
      if (isMockMode()) {
        // Pequeño delay para que skeleton aparezca brevemente — UX realista.
        await new Promise((r) => setTimeout(r, 100));
        const mock = getMockOrder(orderId);
        if (!mock) {
          throw Object.assign(new Error('Mock order not found'), {
            status: 404,
            code: 'ORDER_NOT_FOUND',
          });
        }
        return mock;
      }
      return getOrder(orderId);
    },
    enabled: enabled && !!orderId,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (isTerminalOrderStatus(status)) return false;
      return intervalMs;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
  });
}
